import { useState, useRef } from 'react';
import { LogOut, Settings, ShieldCheck, ChevronDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';

function getInitials(name: string | null, email: string | null): string {
  const src = name || email || '?';
  return src.split(/[\s@]/)[0].slice(0, 2).toUpperCase();
}

function getColor(str: string): string {
  const colors = [
    'bg-emerald-500','bg-sky-500','bg-violet-500',
    'bg-amber-500','bg-rose-500','bg-teal-500',
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export default function UserMenu() {
  const { user, profile, signOut, setShowAuthModal } = useAuth();
  const navigate  = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  if (!user) {
    return (
      <button
        onClick={() => setShowAuthModal(true)}
        className="text-xs font-bold bg-primary text-primary-foreground px-3 py-2 rounded-full hover:opacity-90 transition-opacity whitespace-nowrap"
      >
        Sign In
      </button>
    );
  }

  const initials = getInitials(profile?.display_name ?? null, user.email ?? null);
  const color    = getColor(user.id);
  const label    = profile?.display_name || user.email?.split('@')[0] || 'Account';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 rounded-full pr-2 hover:bg-muted/50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
          {initials}
        </div>
        <span className="text-xs font-semibold text-foreground max-w-[80px] truncate hidden sm:block">{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden sm:block" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 z-50 bg-background border border-border rounded-2xl shadow-xl w-52 py-2 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border/50">
              <p className="text-sm font-bold text-foreground truncate">{label}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
            {profile?.is_admin && (
              <button
                onClick={() => { setOpen(false); navigate(ROUTE_PATHS.ADMIN); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-amber-600 font-semibold hover:bg-muted/50 transition-colors"
              >
                <ShieldCheck className="w-4 h-4" /> Admin Panel
              </button>
            )}
            <button
              onClick={() => { setOpen(false); navigate(ROUTE_PATHS.SAVED_LISTS); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors"
            >
              <Settings className="w-4 h-4 text-muted-foreground" /> My Saved Lists
            </button>
            <button
              onClick={() => { setOpen(false); signOut(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
